import { IconExport, IconMessage } from 'assets/icons';
import { getEnvConstant } from 'constants/env';
import { SALE_DETAIL_PATH, SALE_LIST_PATH } from 'constants/path';
import { useIsMobile } from 'hooks/common';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Booking,
  BookingState,
  OrderTransactionActionStatus,
  TicketState,
} from 'types/api';
import {
  callWebviewHandler,
  isWebview,
  WebviewOperatorType,
} from 'utils/webview';

const UserDetailInfoBox = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;

  p[data-type='total-count'] {
    margin: 0px 0 16px;

    font-weight: 500;
    font-size: 16px;
    line-height: 22px;

    color: rgba(70, 71, 76, 1);
  }
`;

const UserCardDetailBox = styled.div<{ $isLast: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;

  margin-bottom: 24px;
  padding-bottom: ${({ $isLast }) => ($isLast ? '0px' : '24px')};
  border-bottom: ${({ $isLast }) => ($isLast ? '0px' : '1px solid')};
  border-color: rgba(234, 235, 236, 1);
`;

const DataRow = styled.div<{ $gap?: string }>`
  display: flex;
  align-items: flex-start;
  gap: ${({ $gap }) => ($gap ? $gap : '12px')};

  p[data-type='ticket-title'] {
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;

    color: rgba(65, 75, 86, 1);

    &.cancel {
      text-decoration: line-through;
    }
  }

  p[data-type='booking-text'] {
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;

    color: rgba(152, 155, 162, 1);
  }

  p[data-type='bookingId-text'] {
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;

    color: rgba(152, 155, 162, 1);
  }

  p[data-type='query-text'] {
    min-width: 25px;

    font-weight: 500;
    font-size: 14px;
    line-height: 20px;

    color: rgba(70, 71, 76, 1);
  }

  p[data-type='answer-text'] {
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;

    color: rgba(112, 115, 124, 1);
  }
`;

const WhiteBookingItemBoxContainer = styled.div`
  display: flex;
  flex-direction: column;

  border: 1px solid;
  border-color: rgba(234, 235, 236, 1);
  border-radius: 16px;

  padding: 20px 16px;

  background-color: #fff;

  > div[data-type='single-query'] {
    display: flex;
    flex-direction: column;
    gap: 8px;

    margin-bottom: 16px;
    padding-bottom: 16px;

    border-bottom: 1px solid;
    border-color: rgba(234, 235, 236, 1);

    &.last {
      padding-bottom: 0px;
      margin-bottom: 0px;
      border-bottom: 0;
    }
  }
`;

const WhiteQueryBoxContainer = styled.div`
  display: flex;
  flex-direction: column;

  border: 1px solid;
  border-color: rgba(234, 235, 236, 1);
  border-radius: 16px;

  padding: 16px;

  background-color: #fff;

  > div[data-type='single-query'] {
    display: flex;
    flex-direction: column;
    gap: 8px;

    margin-bottom: 16px;
    padding-bottom: 16px;

    border-bottom: 1px solid;
    border-color: rgba(234, 235, 236, 1);

    &.last {
      padding-bottom: 0px;
      margin-bottom: 0px;
      border-bottom: 0;
    }
  }
`;

const Badge = styled.div<{
  $status: BookingState | OrderTransactionActionStatus | TicketState;
}>`
  height: 24px;
  min-width: 51px;
  width: fit-content;

  padding: 0px 6px;

  border-radius: 6px;

  font-weight: 600;
  font-size: 11px;
  line-height: 24px;

  ${({ $status }) => `
    background-color: ${
      $status === BookingState.ABSENT
        ? 'rgba(255, 230, 230, 1)'
        : $status === OrderTransactionActionStatus.CANCELLATION_REQUESTED
        ? 'rgba(255, 230, 230, 1)'
        : $status === OrderTransactionActionStatus.CANCELLATION_COMPLETED
        ? 'rgba(255, 230, 230, 1)'
        : $status === BookingState.APPLIED
        ? 'rgba(234, 235, 236, 1)'
        : $status === BookingState.CANCELED
        ? 'rgba(255, 230, 230, 1)'
        : $status === BookingState.CONFIRMED
        ? 'rgba(213, 233, 255, 1)'
        : $status === BookingState.USED
        ? 'rgba(213, 233, 255, 1)'
        : 'rgba(255, 235, 238, 1)'
    };

    color: ${
      $status === BookingState.ABSENT
        ? 'rgba(255, 66, 66, 1)'
        : $status === OrderTransactionActionStatus.CANCELLATION_REQUESTED
        ? 'rgba(255, 66, 66, 1)'
        : $status === OrderTransactionActionStatus.CANCELLATION_COMPLETED
        ? 'rgba(255, 66, 66, 1)'
        : $status === BookingState.APPLIED
        ? 'rgba(70, 71, 76, 1)'
        : $status === BookingState.CANCELED
        ? 'rgba(255, 66, 66, 1)'
        : $status === BookingState.CONFIRMED
        ? 'rgba(0, 113, 233, 1)'
        : $status === BookingState.USED
        ? 'rgba(0, 113, 233, 1)'
        : 'rgba(255, 235, 238, 1)'
    };
  `}
`;

const TimelineBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;

  border-top: 1px solid;
  border-color: #eaebec;

  padding-top: 24px;

  > div {
    display: flex;
    gap: 16px;

    > p[data-type='timeline-title'] {
      font-weight: 500;
      font-size: 15px;
      line-height: 22px;

      color: #414b56;
    }

    > p[data-type='timeline-date'] {
      font-weight: 400;
      font-size: 15px;
      line-height: 22px;

      color: #70737c;
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;

  margin-top: 24px;

  > button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;

    width: 93px;
    height: 40px;

    border-radius: 12px;

    font-weight: 600;
    font-size: 14px;
    line-height: 40px;
    color: rgba(65, 75, 86, 1);

    background-color: rgba(234, 235, 236, 1);

    &[data-type='cancel'] {
      background-color: #ffe6e6;
      color: #ff4242;
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

interface Props {
  data: Booking;
  orderTrxData: Record<string, OrderTransactionActionStatus>;
  startDateTimeStamp: number;
}

const ListViewBookedUserDetail = ({
  data,
  orderTrxData,
  startDateTimeStamp,
}: Props) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const goToSalesPage = (orderId: string) => {
    if (isMobile) {
      navigate(`${SALE_DETAIL_PATH}/${orderId}`);
    } else {
      localStorage.setItem('orderId', orderId);
      navigate(SALE_LIST_PATH);
    }
  };

  const goToMessagePage = (orderId: string) => {
    if (isWebview()) {
      callWebviewHandler(WebviewOperatorType.OpenActionUrl, {
        actionUrl: `frip://chattingDetail/O${orderId}`,
      });

      return;
    }

    window.open(`${getEnvConstant('webClientUrl')}/messageDetail/O${orderId}`);
  };

  const totalCounts = data.items.reduce((acc, cur2) => {
    if (cur2.tickets.length > 1) {
      const realTicketList = cur2.tickets.filter(ticket => {
        return ticket?.scheduleTerm?.startedAt === startDateTimeStamp;
      });

      return acc + realTicketList.length;
    }

    return (
      acc +
      cur2.counts.applied +
      cur2.counts.confirmed +
      cur2.counts.used +
      cur2.counts.absent
    );
  }, 0);

  return (
    <UserDetailInfoBox>
      {totalCounts > 1 && <p data-type="total-count">총 {totalCounts}건</p>}
      {/* {data.items.length > 1 && (
        <p data-type="total-count">총 {data.items.length}건</p>
      )} */}

      {data.items.map(({ replies, tickets, booking }, itemIdx) => {
        const { id: bookingId } = booking;

        const bookingOrderData = data.order.items.find(
          data => data.id === bookingId,
        );

        const dependedItemList = bookingOrderData.dependedItems.find(
          dependedItem => {
            return (
              dependedItem.units.filter(unit => {
                return unit.indexNo === itemIdx;
              }).length > 0
            );
          },
        );
        const canceledItem = dependedItemList;

        const ticketStatus = tickets[0].status;

        const bookingStatus = canceledItem
          ? orderTrxData[canceledItem.id]
          : ticketStatus;

        return (
          <UserCardDetailBox
            $isLast={data.items.length - 1 === itemIdx}
            key={`booking-user-detail-${itemIdx}`}
          >
            <WhiteBookingItemBoxContainer>
              <DataRow>
                <Badge $status={bookingStatus}>
                  {getBadgeText(bookingStatus)}
                </Badge>
                <p
                  data-type="ticket-title"
                  className={canceledItem ? 'cancel' : ''}
                >
                  {tickets[0].bookingItem.booking.info.options[0].name}
                </p>
              </DataRow>
            </WhiteBookingItemBoxContainer>

            {replies.length > 0 && (
              <WhiteQueryBoxContainer>
                {replies.map(({ title, answer, selections }, idx) => {
                  const getAnswerText = () => {
                    if (answer) {
                      return answer;
                    }

                    if (selections) {
                      return selections
                        .map(data => data.label)
                        .join(', ');
                    }

                    return '-';
                  };

                  return (
                    <div
                      key={`booking-user-detail-query-${idx}`}
                      data-type="single-query"
                      className={replies.length - 1 === idx ? 'last' : ''}
                    >
                      <DataRow>
                        <p data-type="query-text">질문</p>
                        <p data-type="answer-text">{title}</p>
                      </DataRow>
                      <DataRow>
                        <p data-type="query-text">답변</p>
                        <p data-type="answer-text">
                          {getAnswerText() || '-'}
                        </p>
                      </DataRow>
                    </div>
                  );
                })}
              </WhiteQueryBoxContainer>
            )}
          </UserCardDetailBox>
        );
      })}

      {/* {timeline && (
  <TimelineBox>
  <div>
  <p data-type="timeline-title">예약 확정일</p>
  <p data-type="timeline-date">
    {dayjs(timeline.confirmedAt).format('YYYY년 M월 D일 HH:mm')}
  </p>
  </div>
  <div>
  <p data-type="timeline-title">참석/불참일</p>
  <p data-type="timeline-date">
    {!timeline.usedAt && !timeline.absentAt
      ? '-'
      : dayjs(
          timeline.usedAt
            ? timeline.usedAt
            : timeline.absentAt
            ? timeline.absentAt
            : '-',
        ).format('YYYY년 M월 D일 HH:mm')}
  </p>
  </div>
  </TimelineBox>
  )} */}

      <ButtonContainer>
        <button onClick={() => goToSalesPage(data.order.id)}>
          <IconExport />
          판매내역
        </button>
        <button onClick={() => goToMessagePage(data.order.id)}>
          <IconMessage />
          메시지
        </button>

        {/* {BookingState.ABSENT === attendanceResult && (
        <button
          data-type="cancel"
          onClick={() => onClickCancelAbsent(ticketIdList)}
          >
            불참 취소
        </button>
        )}
        {BookingState.USED === attendanceResult && (
          <button
            data-type="cancel"
            onClick={() => onClickCancelAttendance(ticketIdList)}
            >
            참석 취소
          </button>
        )} */}
      </ButtonContainer>
    </UserDetailInfoBox>
  );
};

export default ListViewBookedUserDetail;

const getBadgeText = (
  bookingState: BookingState | OrderTransactionActionStatus | TicketState,
) => {
  switch (bookingState) {
    case OrderTransactionActionStatus.CANCELLATION_REQUESTED:
      return '취소요청';
    case OrderTransactionActionStatus.CANCELLATION_COMPLETED:
      return '취소완료';
    case BookingState.ABSENT:
      return '예약불참';
    case BookingState.APPLIED:
      return '예약대기';
    case BookingState.CANCELED:
      return '취소완료';
    case BookingState.CONFIRMED:
      return '예약확정';
    case BookingState.USED:
      return '예약참석';
    default:
      return '';
  }
};
